import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, X, Loader2, Send } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

const KEY = "daybreak-dismissed-announcements";
const dismissed = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };

export const AnnouncementBanner = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/announcements").then((r) => setList((Array.isArray(r.data) ? r.data : []).filter((a) => !dismissed().includes(a.ann_id)))).catch(() => {}); }, []);
  if (!list.length) return null;
  const a = list[0];
  const close = () => { localStorage.setItem(KEY, JSON.stringify([...dismissed(), a.ann_id])); setList(list.slice(1)); };
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-4">
      <div data-testid="announcement-banner" className={`flex items-start gap-3 rounded-2xl border px-4 py-3 animate-rise ${a.level === "important" ? "border-brand/60 bg-brand/15" : "border-gold-400/40 bg-gold-400/10"}`}>
        <Megaphone className={`w-5 h-5 mt-0.5 shrink-0 ${a.level === "important" ? "text-rose-600" : "text-gold-300"}`} />
        <p data-testid="announcement-text" className="flex-1 text-sm text-slate-800 whitespace-pre-line">{a.text}<span className="block mt-0.5 text-xs text-slate-500">From {a.by}</span></p>
        <button data-testid="announcement-dismiss" onClick={close} aria-label="Dismiss" className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:text-white hover:bg-white/10"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const AnnouncementsAdmin = () => {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [level, setLevel] = useState("info");
  const [push, setPush] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/announcements").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const post = async () => {
    setBusy(true);
    try { await api.post("/admin/announcements", { text, level, push }); setText(""); toast.success("Announcement posted for everyone"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const end = async (id) => { await api.delete(`/admin/announcements/${id}`).catch(() => {}); load(); };
  return (
    <section data-testid="admin-announcements" className="card p-5 sm:p-6">
      <p className="eyebrow">Announcements</p>
      <h2 className="mt-1 font-display text-3xl text-white">Message every student</h2>
      <textarea data-testid="announcement-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={400} rows={3} placeholder="e.g. Mock exams start Monday. Check the exam planner."
        className="mt-4 w-full rounded-2xl border border-slate-200 bg-surface-deep/60 p-3 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400" />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full bg-slate-100 p-0.5">
          {[["info", "Info"], ["important", "Important"]].map(([k, l]) => (
            <button key={k} data-testid={`announcement-level-${k}`} onClick={() => setLevel(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${level === k ? (k === "important" ? "bg-brand text-white" : "bg-gold-400 text-black") : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700"><Switch data-testid="announcement-push-switch" checked={push} onCheckedChange={setPush} />Also send a notification</label>
        <button data-testid="announcement-post" onClick={post} disabled={busy || !text.trim()} className="ml-auto inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-2.5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Post
        </button>
      </div>
      <ul className="mt-5 space-y-2">
        {list.map((a) => (
          <li key={a.ann_id} data-testid={`announcement-row-${a.ann_id}`} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-surface-2 p-3 text-sm">
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${a.active ? (a.level === "important" ? "bg-brand" : "bg-gold-400") : "bg-slate-300"}`} />
            <span className={`flex-1 ${a.active ? "text-slate-800" : "text-slate-500 line-through"}`}>{a.text}<span className="block text-xs text-slate-500">{new Date(a.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })} · {a.by}</span></span>
            {a.active && <button data-testid={`announcement-end-${a.ann_id}`} onClick={() => end(a.ann_id)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-rose-600 hover:text-white">End</button>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const UsageTrends = () => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/admin/trends").then((r) => setT(r.data ?? null)).catch(() => setT(false)); }, []);
  if (!t) return null;
  const feats = Array.isArray(t.features) ? t.features : [];
  const max = Math.max(1, ...feats.map((f) => f.users));
  const inactive = Array.isArray(t.inactive) ? t.inactive : [];
  return (
    <section data-testid="admin-trends" className="grid gap-4 lg:grid-cols-2">
      <div className="card p-5 sm:p-6">
        <p className="eyebrow">Usage trends · last 7 days</p>
        <h2 className="mt-1 font-display text-3xl text-white">Most-used features</h2>
        <ul className="mt-4 space-y-2.5">
          {feats.slice(0, 12).map((f) => (
            <li key={f.feature} data-testid={`trend-${f.feature}`} className="text-sm">
              <div className="flex justify-between gap-2"><span className="font-semibold capitalize text-slate-800">{f.feature.replace(/-/g, " ")}</span><span className="font-mono text-xs text-slate-500">{f.users} users · {f.hits} uses</span></div>
              <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-gold-500 to-gold-300" style={{ width: `${(100 * f.users) / max}%` }} /></div>
            </li>
          ))}
          {!feats.length && <li className="text-sm text-slate-500">No usage recorded yet.</li>}
        </ul>
      </div>
      <div className="card p-5 sm:p-6">
        <p className="eyebrow">Gone quiet</p>
        <h2 className="mt-1 font-display text-3xl text-white">Not opened in a week · {inactive.length}</h2>
        <ul className="mt-4 space-y-2 max-h-80 overflow-y-auto">
          {inactive.map((u) => (
            <li key={u.user_id} data-testid={`inactive-${u.user_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-surface-2 px-3 py-2 text-sm">
              <span className="min-w-0"><span className="block font-semibold text-slate-800 truncate">{u.name || u.email}</span><span className="block text-xs text-slate-500 truncate">{u.email}</span></span>
              <span className="shrink-0 font-mono text-xs text-rose-600">{u.last_seen ? `last ${u.last_seen}` : "never"}</span>
            </li>
          ))}
          {!inactive.length && <li className="text-sm text-slate-500">Everyone has been active this week.</li>}
        </ul>
      </div>
    </section>
  );
};
